/**
 * PWA / Web Push API 클라이언트.
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §6.8
 */
import apiClient from '@/lib/api/client'
import type {
  AdminPushSendRequest,
  PushSendLogResponse,
  PushSendResultResponse,
  PushSubscribeRequest,
  PushSubscriptionResponse,
  VapidPublicKeyResponse,
} from './types'

interface ApiEnvelope<T> {
  success: boolean
  data: T
  meta?: {
    page: number
    size: number
    totalElements: number
    totalPages: number
  }
}

interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

// ─────────────────────────────────────────────────────────────────────────────
// VAPID
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchVapidPublicKey(): Promise<string | null> {
  const { data } = await apiClient.get<ApiEnvelope<VapidPublicKeyResponse>>(
    '/pwa/vapid-public-key'
  )
  return data.data.publicKey
}

// ─────────────────────────────────────────────────────────────────────────────
// 푸시 구독 (사용자)
// ─────────────────────────────────────────────────────────────────────────────

export async function subscribePush(
  request: PushSubscribeRequest
): Promise<PushSubscriptionResponse> {
  const { data } = await apiClient.post<ApiEnvelope<PushSubscriptionResponse>>(
    '/push/subscriptions',
    request
  )
  return data.data
}

export async function unsubscribePush(subscriptionId: number): Promise<void> {
  await apiClient.delete(`/push/subscriptions/${subscriptionId}`)
}

export async function fetchMySubscriptions(): Promise<PushSubscriptionResponse[]> {
  const { data } = await apiClient.get<ApiEnvelope<PushSubscriptionResponse[]>>(
    '/push/subscriptions/me'
  )
  return data.data
}

// ─────────────────────────────────────────────────────────────────────────────
// 어드민 발송
// ─────────────────────────────────────────────────────────────────────────────

export async function sendAdminPush(
  request: AdminPushSendRequest
): Promise<PushSendResultResponse> {
  const { data } = await apiClient.post<ApiEnvelope<PushSendResultResponse>>(
    '/admin/push/send',
    request
  )
  return data.data
}

export async function scheduleAdminPush(
  request: AdminPushSendRequest
): Promise<PushSendLogResponse> {
  const { data } = await apiClient.post<ApiEnvelope<PushSendLogResponse>>(
    '/admin/push/schedule',
    request
  )
  return data.data
}

export async function fetchAdminPushLogs(
  page = 0,
  size = 20
): Promise<PageResponse<PushSendLogResponse>> {
  const { data } = await apiClient.get<ApiEnvelope<PageResponse<PushSendLogResponse>>>(
    `/admin/push/logs?page=${page}&size=${size}`
  )
  return data.data
}
